import React from 'react';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error to console for debugging
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    // Handle different types of errors
    if (error instanceof Event) {
      console.log('Event object caught by error boundary - likely a browser event error');
      // Reset the error boundary for event errors
      this.setState({ hasError: false, error: null });
      return;
    }
    
    // Check if it's a Firebase authentication error
    if (error && typeof error === 'object' && error.message && error.message.includes('Firebase: IdP denied access')) {
      console.log('Firebase authentication was cancelled by user - this is normal behavior');
      // Don't show error boundary for user-cancelled authentication
      this.setState({ hasError: false, error: null });
    }
  }

  render() {
    if (this.state.hasError) {
      // Check if it's an Event object that we should ignore
      if (this.state.error instanceof Event) {
        // Reset the error boundary for event errors
        this.setState({ hasError: false, error: null });
        return this.props.children;
      }
      
      // Check if it's a Firebase auth error that we should ignore
      if (this.state.error && typeof this.state.error === 'object' && this.state.error.message && 
          this.state.error.message.includes('Firebase: IdP denied access')) {
        // Reset the error boundary for Firebase auth cancellations
        this.setState({ hasError: false, error: null });
        return this.props.children;
      }

      // Show fallback UI for other errors
      return (
        <div className="flex items-center justify-center min-h-screen bg-gray-50">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 19.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Something went wrong</h2>
            <p className="text-gray-600 mb-4">An unexpected error occurred. Please try refreshing the page.</p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
